import { Avatar, Box, Typography, alpha, useTheme } from '@mui/material';
import { Link, type To } from 'react-router-dom';

import { TimeAgo } from '@modules/_shared/components/TimeAgo';

export const StatusDot = ({ color }: { color: string }) => (
  <Box
    component="span"
    sx={{
      width: 8,
      height: 8,
      borderRadius: '50%',
      bgcolor: color,
      flexShrink: 0,
    }}
  />
);

const SubtitleSeparator = () => (
  <Box component="span" sx={{ color: 'text.disabled' }}>
    •
  </Box>
);

export const SubtitleTruncatedItem = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <>
    <SubtitleSeparator />
    <Box
      component="span"
      sx={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Box>
  </>
);

type LeadCardRowProps = {
  icon: React.ReactNode;
  color: 'primary' | 'secondary' | 'warning';
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  date?: React.ReactNode;
  to?: To;
  external?: boolean;
  onClick?: () => void;
};

export const LeadCardRow = ({
  icon,
  color,
  title,
  subtitle,
  date,
  to,
  external = false,
  onClick,
}: LeadCardRowProps) => {
  const { palette } = useTheme();

  const linkProps =
    to != null
      ? {
          component: Link,
          to,
          ...(external && { target: '_blank', rel: 'noopener noreferrer' }),
        }
      : { component: 'div' as const, onClick, role: 'button', tabIndex: 0 };

  return (
    <Box
      {...linkProps}
      sx={{
        display: 'flex',
        cursor: 'pointer',
        alignItems: 'center',
        gap: 1.25,
        px: 1.5,
        py: 1,
        mx: -1.5,
        borderRadius: '8px',
        textDecoration: 'none',
        color: 'inherit',
        transition: 'background-color 0.15s ease',
        '&:hover': { bgcolor: 'grey.50' },
      }}
    >
      <Avatar
        sx={{
          width: 36,
          height: 36,
          flexShrink: 0,
          bgcolor: alpha(palette[color].main, 0.08),
          color: palette[color].main,
        }}
      >
        {icon}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          noWrap
          sx={{ fontWeight: 500, lineHeight: 1.3 }}
        >
          {title}
        </Typography>
        {subtitle != null && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 0,
              color: 'text.secondary',
              typography: 'caption',
              lineHeight: 1.3,
            }}
          >
            {subtitle}
          </Box>
        )}
      </Box>
      {date != null && (
        <Typography
          variant="caption"
          component="div"
          sx={{
            color: 'text.disabled',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          {typeof date === 'string' ? <TimeAgo dateString={date} /> : date}
        </Typography>
      )}
    </Box>
  );
};
